import { track, type SimAskKind, type SimName, type UploadVia } from "@/lib/analytics"
import { SIM_FILE_SIZE_MESSAGE, SIM_FILE_TYPE_MESSAGE } from "../oio-sim/files"

/**
 * Product analytics shared by both order simulators. Counts, kinds, codes and booleans only:
 * never a filename, a document's text, an answer or a question.
 */

/** pdf / docx / image counts for a set of files, from the extension. */
export function fileKinds(files: File[]): { pdf: number; docx: number; image: number } {
  const n = { pdf: 0, docx: 0, image: 0 }
  for (const f of files) {
    if (/\.pdf$/i.test(f.name)) n.pdf += 1
    else if (/\.docx$/i.test(f.name)) n.docx += 1
    else n.image += 1
  }
  return n
}

/** Why the server refused an upload, as a code. */
export function uploadFailReason(err: unknown): "too_large" | "unsupported" | "server" {
  const status = (err as { status?: number } | null)?.status
  if (status === 413) return "too_large"
  if (status === 415 || status === 422) return "unsupported"
  return "server"
}

/** Files that went in. */
export function trackUploaded(sim: SimName, files: File[], via: UploadVia): void {
  if (files.length) track("sim_document_uploaded", { sim, count: files.length, via, ...fileKinds(files) })
}

export function trackUploadFailed(sim: SimName, reason: "too_large" | "unsupported" | "server"): void {
  track("sim_upload_failed", { sim, reason })
}

/** The files `filterSimFiles` refused, by the reason its message gives. */
export function trackRefused(sim: SimName, message: string): void {
  if (message === SIM_FILE_TYPE_MESSAGE) trackUploadFailed(sim, "unsupported")
  else if (message === SIM_FILE_SIZE_MESSAGE) trackUploadFailed(sim, "too_large")
}

export type AskAnswer = "option" | "text" | "upload" | "not_available" | "already_in" | "resume" | "stop_here"

export function trackAskShown(sim: SimName, kind: SimAskKind, stage: string): void {
  track("sim_ask_shown", { sim, kind, stage })
}

export function trackAskAnswered(sim: SimName, kind: SimAskKind, answer: AskAnswer): void {
  track("sim_ask_answered", { sim, kind, answer })
}

/** The reader chose to stop with a partial draft: how many sections were written of how many expected. */
export function trackStoppedEarly(sim: SimName, written: number, total: number): void {
  track("sim_stopped_early", { sim, written, total })
}

const runStart = new Map<string, number>()
const completed = new Set<string>()

/** A run began (live or replayed): its time, for the draft's duration. The first run of a draft is the one measured. */
export function noteRunStart(threadId: string, createdAt?: string): void {
  if (runStart.has(threadId)) return
  const t = createdAt ? Date.parse(createdAt) : NaN
  runStart.set(threadId, Number.isNaN(t) ? Date.now() : t)
}

/** The draft became ready in a live run: once per draft, with the sections written and the time from the first run. */
export function trackDraftCompleted(sim: SimName, threadId: string, draft: { sections: Record<string, { markdown: string } | undefined>; expected?: string[] | null } | null): void {
  if (completed.has(threadId)) return
  const sections = Object.values(draft?.sections ?? {}).filter((s) => (s?.markdown ?? "").trim() !== "").length
  if (!sections || sections < (draft?.expected?.length ?? 0)) return // a partial draft is not a completed one
  completed.add(threadId)
  const t0 = runStart.get(threadId)
  track("sim_draft_completed", { sim, sections, duration_ms: t0 === undefined ? 0 : Math.max(0, Date.now() - t0) })
}
